import React, { useState, useEffect } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';
import './MoreFilters.css';
import { FilterOptions } from '../FilterBar';
import { useCampContext } from '../../context/CampContext';

const activityKeywordsOptions = [
  { value: 'sports', label: 'Sports', description: 'General sports activities including team sports, individual sports, and athletic training' },
  { value: 'basketball', label: 'Basketball', description: 'Basketball training, games, and tournaments' },
  { value: 'soccer', label: 'Soccer', description: 'Soccer training, games, and tournaments' },
  { value: 'swimming', label: 'Swimming', description: 'Swimming lessons, water safety, and aquatic activities' },
  { value: 'art', label: 'Art', description: 'Creative arts including painting, drawing, sculpture, and crafts' },
  { value: 'music', label: 'Music', description: 'Music lessons, band, choir, and musical performances' },
  { value: 'dance', label: 'Dance', description: 'Dance classes, choreography, and performances' },
  { value: 'science', label: 'Science', description: 'Science experiments, STEM activities, and educational programs' },
  { value: 'technology', label: 'Technology', description: 'Computer programming, robotics, and technology education' },
  { value: 'cooking', label: 'Cooking', description: 'Cooking classes, nutrition education, and culinary skills' },
  { value: 'outdoor', label: 'Outdoors', description: 'Outdoor activities, nature exploration, and environmental education' },
  { value: 'adventure', label: 'Adventure', description: 'Adventure activities, rock climbing, and outdoor challenges' },
  { value: 'theater', label: 'Theater', description: 'Drama, acting, and theatrical performances' },
  { value: 'photography', label: 'Photography', description: 'Photography lessons and creative visual arts' },
  { value: 'fitness', label: 'Fitness', description: 'Fitness training, yoga, and wellness activities' }
];

interface MoreFiltersProps {
  onFiltersChange: (filters: FilterOptions) => void;
  onClearAll: () => void;
}



const MoreFilters: React.FC<MoreFiltersProps> = ({
  onFiltersChange,
  onClearAll,
}) => {
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const { filters, setFilters } = useCampContext();

  useEffect(() => {
    if (showFilterPopup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showFilterPopup]);

  const hasActiveFilters = () => {
    return (
      (filters.activityKeywords && filters.activityKeywords.length > 0) ||
      (filters.minPrice !== undefined && filters.minPrice !== null) ||
      (filters.maxPrice !== undefined && filters.maxPrice !== null) ||
      (filters.duration) ||
      (filters.minRating !== undefined && filters.minRating !== null)
    );
  };

  const handleFilterChange = (key: keyof FilterOptions, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters as FilterOptions);
  };

  const handleApplyFilters = () => {
    console.log('handleApplyFilters', filters);
    onFiltersChange(filters as FilterOptions);
    setShowFilterPopup(false);
    setFilters(filters as FilterOptions);
  };

  const handleClearAll = () => {
    const clearedFilters: FilterOptions = {
      activityKeywords: [],
      maxPrice: undefined,
      minPrice: undefined,
      duration: undefined,
      minRating: undefined
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    onClearAll();
    setShowFilterPopup(false);
  };

  return (
    <>
      <button
        className="filter-button filter-button--more"
        onClick={() => setShowFilterPopup(!showFilterPopup)}
      >
        <SlidersHorizontal className="icon icon--sm" />
        <span>More Filters</span>
        {hasActiveFilters() && (
          <span className="filter-badge">!</span>
        )}
      </button>
      
      {/* Filter Popup */}
      {showFilterPopup && (
        <div className="filter-popup-overlay" onClick={() => setShowFilterPopup(false)}>
          <div className="filter-popup" onClick={(e) => e.stopPropagation()}>
            <div className="filter-popup-header">
              <div className="filter-popup-title-section">
                <h3>Filter Camps</h3>
                <button 
                  className="filter-clear-all-btn"
                  onClick={handleClearAll}
                >
                  Clear All
                </button>
              </div>
              <button 
                className="filter-popup-close"
                onClick={() => setShowFilterPopup(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="filter-popup-content">
              {/* Cost Section */}
              <div className="filter-section">
                <h4>Cost per day</h4>                
                <div className="price-range">
                  <div className="price-inputs">
                    <div className="price-input">
                      <label>Minimum</label>
                      <input 
                        type="text" 
                        value={filters.minPrice || ''} 
                        onChange={(e) => handleFilterChange('minPrice', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="$20"
                      />
                    </div>
                    <div className="price-input">
                      <label>Maximum</label>
                      <input 
                        type="text" 
                        value={filters.maxPrice || ''} 
                        onChange={(e) => handleFilterChange('maxPrice', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="$1800+"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Camp Duration Section */}
              <div className="filter-section">
                <h4>Camp Duration</h4>
                <div className="duration-toggle">
                  <button 
                    className={`toggle-button ${filters?.duration === 'full-day' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('duration', 'full-day')}
                  >
                    Full Day
                  </button>
                  <button 
                    className={`toggle-button ${filters?.duration === 'half-day' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('duration', 'half-day')}
                  >
                    Half Day
                  </button>
                </div>              
              </div>

              {/* Camp Topics & Themes Section */}
              <div className="filter-section">
                <h4>Camp Topics & Themes</h4>
                <div className="topics-grid">
                  {activityKeywordsOptions.map(keyword => (
                    <label key={keyword.value} className="topic-checkbox">
                      <input 
                        type="checkbox" 
                        checked={filters?.activityKeywords?.includes(keyword.value)}
                        onChange={(e) => {
                          const newActivityKeywords = e.target.checked 
                            ? [...filters?.activityKeywords || [], keyword.value]
                            : filters?.activityKeywords?.filter(t => t !== keyword.value);
                          handleFilterChange('activityKeywords', newActivityKeywords);
                        }}
                      />
                      <span>{keyword.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Minimum Provider Reviews Section */}
              <div className="filter-section">
                <h4>Minimum Provider Reviews</h4>
                <div className="rating-buttons">
                  <button 
                    className={`rating-button ${filters?.minRating === 3.5 ? 'active' : ''}`}
                    onClick={() => handleFilterChange('minRating', filters?.minRating === 3.5 ? null : 3.5)}
                  >
                    ☆ 3.5+
                  </button>
                  <button 
                    className={`rating-button ${filters?.minRating === 4 ? 'active' : ''}`}
                    onClick={() => handleFilterChange('minRating', filters?.minRating === 4 ? null : 4)}
                  >
                    ☆ 4+
                  </button>
                  <button 
                    className={`rating-button ${filters?.minRating === 4.5 ? 'active' : ''}`}
                    onClick={() => handleFilterChange('minRating', filters?.minRating === 4.5 ? null : 4.5)}
                  >
                    ☆ 4.5+
                  </button>
                </div>
              </div>
            </div>
            
            <div className="filter-popup-footer">
              <button className="close-button" onClick={() => setShowFilterPopup(false)}>
                Close
              </button>
              <button className="apply-button" onClick={handleApplyFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MoreFilters;
